import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { isRelation } from "@/models/utils/semanticChecks";

import { getStaticBracketLayoutWidth } from "../../utils/groups/useGetBracketsSvgPathsBySymbol";
import { groupNodesWithAttachments } from "../utils";

import { getContainerParameters, isWrappable } from "./isWrappable";

import type { InlineNodeContentRenderItem } from "../types";
import type { Atom, WrappableContainer } from "./types";

type WidthLookup = (key: string, fallback: number) => number;

type BuildAtomStreamOptions = {
  widths: Record<string, number>;
};

const DEFAULT_COMMA_WIDTH = 5;
const DEFAULT_EMPTY_PARAMETER_WIDTH = 10;

type WalkContext = {
  lookup: WidthLookup;
};

export function buildAtomStream(
  items: InlineNodeContentRenderItem[],
  options: BuildAtomStreamOptions,
): Atom[] {
  const ctx: WalkContext = {
    lookup: (key, fallback) => {
      const measured = options.widths[key];
      return typeof measured === "number" && measured >= 0 ? measured : fallback;
    },
  };

  return walkItems(items, 0, ctx);
}

function walkItems(items: InlineNodeContentRenderItem[], depth: number, ctx: WalkContext): Atom[] {
  return items.flatMap((item) => {
    const baseNode = item.node;

    if (baseNode && item.attachmentNodes.length === 0 && isWrappable(baseNode)) {
      return emitContainer(baseNode, depth, ctx);
    }

    return [makeItemAtom(item, depth, ctx.lookup)];
  });
}

function getSymbolMetrics(container: WrappableContainer, ctx: WalkContext) {
  if (!("symbolNode" in container)) {
    return null;
  }

  const placement = container.is(FunctionNodeModel) ? container.placement : "prefix";
  const width = ctx.lookup(`${container.id}-symbol`, 0);

  return {
    width,
    placement,
  };
}

function emitContainer(container: WrappableContainer, depth: number, ctx: WalkContext): Atom[] {
  const openKey = `${container.id}-open`;
  const closeKey = `${container.id}-close`;

  const openFallback = getStaticBracketLayoutWidth(container.openingSymbol);
  const closeFallback = getStaticBracketLayoutWidth(container.closingSymbol);
  const { width: symbolWidth = 0, placement } = getSymbolMetrics(container, ctx) ?? {};

  return [
    {
      kind: "open",
      key: openKey,
      container,
      depth,
      width: ctx.lookup(openKey, openFallback) + (placement === "prefix" ? symbolWidth : 0),
    },
    ...emitParameters(container, depth, ctx),
    {
      kind: "close",
      key: closeKey,
      container,
      depth,
      width: ctx.lookup(closeKey, closeFallback) + (placement === "postfix" ? symbolWidth : 0),
    },
  ];
}

function emitParameters(container: WrappableContainer, depth: number, ctx: WalkContext): Atom[] {
  const parameters = getContainerParameters(container);

  return parameters.flatMap((inline, paramIndex) => {
    const isLast = paramIndex === parameters.length - 1;
    const innerItems = groupNodesWithAttachments(inline.nodes);
    let atoms = walkItems(innerItems, depth + 1, ctx);

    if (atoms.length === 0) {
      const emptyKey = `${inline.id}-empty`;
      atoms = [
        {
          kind: "empty-parameter",
          key: emptyKey,
          parentInline: inline,
          depth: depth + 1,
          width: ctx.lookup(emptyKey, DEFAULT_EMPTY_PARAMETER_WIDTH),
        },
      ];
    }

    if (isLast) return atoms;

    const commaKey = `${container.id}-comma-${paramIndex}`;
    atoms.push({
      kind: "comma",
      key: commaKey,
      container,
      depth: depth + 1,
      width: ctx.lookup(commaKey, DEFAULT_COMMA_WIDTH),
      parameterIndex: paramIndex,
    });
    return atoms;
  });
}

function makeItemAtom(item: InlineNodeContentRenderItem, depth: number, lookup: WidthLookup): Atom {
  const isOperator = !!(item.node && item.node.is(OperatorSymbolNodeModel));
  const isRelationOperator = isRelation(item.node);

  return {
    kind: "item",
    key: item.key,
    item,
    depth,
    width: lookup(item.key, 0),
    isOperator,
    isRelationOperator,
  };
}
